import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

// Get JWT token from localStorage
const getAuthToken = (): string | null => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem('authToken');
  }
  return null;
};

// Base query with JWT token injection
const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_BASE_URL || 'https://nova.rocketproxy7.shop/api',
  prepareHeaders: (headers) => {
    headers.set('Content-Type', 'application/json');
    
    // Add JWT token to Authorization header if available
    const token = getAuthToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    
    return headers;
  },
});

// Custom base query that handles authentication and converts query params
const baseQueryWithAuth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  // Normalize args to FetchArgs format
  const normalizedArgs: FetchArgs =
    typeof args === 'string'
      ? { url: args, method: 'GET' }
      : { ...args };

  const originalMethod = normalizedArgs.method || 'GET';

  // For GET requests, convert query params from body to URL params
  if (originalMethod === 'GET' && normalizedArgs.body && typeof normalizedArgs.body === 'object') {
    const url = new URL(normalizedArgs.url, 'http://localhost');
    const body = normalizedArgs.body as Record<string, unknown>;
    
    // Add query params from body to URL
    Object.entries(body).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url.searchParams.append(key, String(value));
      }
    });
    
    // Remove body from GET request
    normalizedArgs.url = url.pathname + url.search;
    delete normalizedArgs.body;
  }

  const result = await baseQuery(normalizedArgs, api, extraOptions);

  // Handle rate limiting
  if (result.error && 'status' in result.error && result.error.status === 429) {
    return {
      error: {
        status: 429,
        data: {
          success: false,
          error: 'Too many requests. Please try again later.',
        },
      },
    };
  }

  // Handle authentication errors
  if (result.error && 'status' in result.error && result.error.status === 401) {
    // Unauthorized - clear auth data
    if (typeof window !== 'undefined') {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      // Optionally redirect to login page
      // window.location.href = '/login';
    }
  }

  return result;
};

export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['User', 'Order', 'Transaction', 'Settings', 'Dashboard', 'PaymentMethod', 'ProviderConfig', 'ManualService', 'AuditLog'],
  endpoints: () => ({}),
});
